import {
  COMMUNICATION_SELECT_CHEVRON_STYLE,
  COMMUNICATION_SELECT_CLASS,
  DELIVERY_MODE_OPTIONS,
  DeliveryMode,
  SUMMARY_FREQUENCY_OPTIONS,
  SummaryFrequency,
  US_TIMEZONE_OPTIONS,
  WEEK_DAY_OPTIONS,
  WeekDay,
} from "../../constants/communicationConstants";

interface DeliveryModeSectionProps {
  deliveryMode: DeliveryMode;
  summaryTime: string;
  summaryFrequency: SummaryFrequency;
  summaryDayOfWeek: WeekDay;
  timezone: string;
  onDeliveryModeChange: (mode: DeliveryMode) => void;
  onSummaryTimeChange: (time: string) => void;
  onSummaryFrequencyChange: (frequency: SummaryFrequency) => void;
  onSummaryDayOfWeekChange: (day: WeekDay) => void;
  onTimezoneChange: (timezone: string) => void;
  readOnly?: boolean;
  radioGroupName?: string;
  footerNote?: string;
}

/** Same icon slot as COMMUNICATION_SELECT_CLASS (`right 0.75rem`, 16px). */
const timeInputClassName =
  "relative w-full border border-dropDownBorder rounded-md pl-3 pr-10 py-2 text-royalBlue bg-white bg-no-repeat bg-[length:16px_16px] bg-[right_0.75rem_center] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0";

const TIME_INPUT_ICON_STYLE = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'%3E%3Ccircle cx='8' cy='8' r='5.25' stroke='%23172B4D' stroke-width='1.5'/%3E%3Cpath d='M8 5.5V8l2 1.25' stroke='%23172B4D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
} as const;

function DeliveryModeSection({
  deliveryMode,
  summaryTime,
  summaryFrequency,
  summaryDayOfWeek,
  timezone,
  onDeliveryModeChange,
  onSummaryTimeChange,
  onSummaryFrequencyChange,
  onSummaryDayOfWeekChange,
  onTimezoneChange,
  readOnly = false,
  radioGroupName = "deliveryMode",
  footerNote,
}: DeliveryModeSectionProps): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {DELIVERY_MODE_OPTIONS.map((option) => (
          <label
            key={option.key}
            className={`flex items-center gap-2 ${
              readOnly ? "opacity-70" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name={radioGroupName}
              value={option.key}
              checked={deliveryMode === option.key}
              disabled={readOnly}
              onChange={() => onDeliveryModeChange(option.key)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm font-medium text-royalBlue">
              {option.label}
            </span>
          </label>
        ))}
      </div>

      {deliveryMode === DeliveryMode.SUMMARY && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor={`${radioGroupName}-summaryFrequency`}
                className="block text-sm font-medium text-royalBlue mb-1"
              >
                Frequency
              </label>
              <select
                id={`${radioGroupName}-summaryFrequency`}
                value={summaryFrequency}
                disabled={readOnly}
                onChange={(e) =>
                  onSummaryFrequencyChange(e.target.value as SummaryFrequency)
                }
                className={COMMUNICATION_SELECT_CLASS}
                style={COMMUNICATION_SELECT_CHEVRON_STYLE}
              >
                {SUMMARY_FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {summaryFrequency === SummaryFrequency.WEEKLY ? (
              <div>
                <label
                  htmlFor={`${radioGroupName}-summaryDayOfWeek`}
                  className="block text-sm font-medium text-royalBlue mb-1"
                >
                  Day of week
                </label>
                <select
                  id={`${radioGroupName}-summaryDayOfWeek`}
                  value={summaryDayOfWeek}
                  disabled={readOnly}
                  onChange={(e) =>
                    onSummaryDayOfWeekChange(e.target.value as WeekDay)
                  }
                  className={COMMUNICATION_SELECT_CLASS}
                  style={COMMUNICATION_SELECT_CHEVRON_STYLE}
                >
                  {WEEK_DAY_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="hidden md:block" />
            )}

            <div>
              <label
                htmlFor={`${radioGroupName}-summaryTime`}
                className="block text-sm font-medium text-royalBlue mb-1"
              >
                Time
              </label>
              <input
                id={`${radioGroupName}-summaryTime`}
                type="time"
                value={summaryTime}
                disabled={readOnly}
                onChange={(e) => onSummaryTimeChange(e.target.value)}
                className={timeInputClassName}
                style={TIME_INPUT_ICON_STYLE}
              />
            </div>

            <div>
              <label
                htmlFor={`${radioGroupName}-timezone`}
                className="block text-sm font-medium text-royalBlue mb-1"
              >
                Time zone
              </label>
              <select
                id={`${radioGroupName}-timezone`}
                value={timezone}
                disabled={readOnly}
                onChange={(e) => onTimezoneChange(e.target.value)}
                className={COMMUNICATION_SELECT_CLASS}
                style={COMMUNICATION_SELECT_CHEVRON_STYLE}
              >
                {US_TIMEZONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p
            className={`text-xs ${
              footerNote ? "text-primary" : "text-textSecondaryGray"
            }`}
          >
            {footerNote ??
              `Your ${
                summaryFrequency === SummaryFrequency.WEEKLY
                  ? "weekly"
                  : "daily"
              } summary includes updates and pending approvals.`}
          </p>
        </div>
      )}
    </div>
  );
}

export default DeliveryModeSection;
